import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/i18n";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** `/agents/wiki-keeper/passes`: what Kosmas checked, filed for Metoděj and wrote himself. */

type Outcome = {
  title: string;
  scope: "project" | "company";
  action: "created" | "written" | "skipped";
  globalId?: number;
  slug?: string;
  reason?: string;
  warning?: string;
};

type ProjectResult = {
  project: string;
  summary: string;
  outcomes: Outcome[];
  seedFound?: boolean;
  seed?: {
    checked: number;
    present: number;
    filed: string[];
    noPassword: string[];
    skipped?: string;
  };
};

type PassResult = {
  skipped?: boolean;
  reason?: string;
  quiet?: number;
  results?: ProjectResult[];
  failures?: Array<{ project: string; error: string }>;
  companyNote?: string | null;
};

type Pass = {
  id: string;
  type: "wiki_keeper" | "wiki_audit";
  status: string;
  startedAt: string;
  finishedAt: string | null;
  result: PassResult | null;
  error: string | null;
};

function OutcomeLine({ o }: { o: Outcome }) {
  const { t } = useTranslation();
  if (o.action === "created" && o.globalId) {
    return (
      <li>
        {t("agentTabs.wikiKeeper.ticketFor")}{" "}
        <Link to="/work-item/$id" params={{ id: String(o.globalId) }} className="underline">
          #{o.globalId} {o.title}
        </Link>
        {o.warning ? <span className="text-muted-foreground"> ({o.warning})</span> : null}
      </li>
    );
  }
  if (o.action === "written") {
    return (
      <li>
        {t("agentTabs.wikiKeeper.companyWiki")}{" "}
        <Link to="/wiki/$" params={{ _splat: o.slug ?? "" }} className="underline">
          {o.title}
        </Link>
      </li>
    );
  }
  return (
    <li className="text-muted-foreground">
      {t("agentTabs.wikiKeeper.skipped", { title: o.title, reason: o.reason })}
    </li>
  );
}

function SeedLine({ r }: { r: ProjectResult }) {
  const { t } = useTranslation();
  if (!r.seed) return null;
  if (!r.seedFound)
    return <p className="text-muted-foreground text-xs">{t("agentTabs.wikiKeeper.seedMissing")}</p>;
  const { checked, present, filed, noPassword, skipped } = r.seed;
  return (
    <p className="text-xs">
      {t("agentTabs.wikiKeeper.seed", { checked, present })}
      {filed.length > 0 ? t("agentTabs.wikiKeeper.seedFiled", { list: filed.join(", ") }) : ""}
      {noPassword.length > 0
        ? t("agentTabs.wikiKeeper.seedNoPassword", { list: noPassword.join(", ") })
        : ""}
      {skipped ? ` (${skipped})` : ""}
    </p>
  );
}

export function WikiKeeperPassesPage() {
  const { t } = useTranslation();
  const [passes, setPasses] = useState<Pass[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api
        .get<{ passes: Pass[] }>("/api/agents/wiki-keeper/passes")
        .then((res) => setPasses(res.passes))
        .catch(() => setMessage(t("agentTabs.wikiKeeper.loadFailed"))),
    [t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function start(mode: "watch" | "audit") {
    setMessage(null);
    try {
      await api.post("/api/agents/wiki-keeper/run", { mode });
      setMessage(
        mode === "audit"
          ? t("agentTabs.wikiKeeper.auditStarted")
          : t("agentTabs.wikiKeeper.watchStarted"),
      );
      setTimeout(() => void load(), 3000);
    } catch {
      setMessage(t("agentTabs.wikiKeeper.startFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => void start("watch")}>
          {t("agentTabs.wikiKeeper.checkNow")}
        </Button>
        <Button size="sm" variant="outline" onClick={() => void start("audit")}>
          {t("agentTabs.wikiKeeper.runAudit")}
        </Button>
        {message ? <span className="text-muted-foreground text-sm">{message}</span> : null}
      </div>

      {passes === null ? (
        <p className="text-muted-foreground text-sm">{t("agentTabs.wikiKeeper.loading")}</p>
      ) : passes.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("agentTabs.wikiKeeper.none")}</p>
      ) : (
        passes.map((p) => (
          <Card key={p.id}>
            <CardContent className="space-y-2 pt-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">
                  {p.type === "wiki_audit"
                    ? t("agentTabs.wikiKeeper.typeAudit")
                    : t("agentTabs.wikiKeeper.typeChanges")}
                </Badge>
                <span>{formatDateTime(p.startedAt)}</span>
                <Badge variant={p.status === "failed" ? "destructive" : "secondary"}>
                  {p.status}
                </Badge>
                {p.result?.skipped ? (
                  <span className="text-muted-foreground">{p.result.reason}</span>
                ) : null}
                {typeof p.result?.quiet === "number" ? (
                  <span className="text-muted-foreground">
                    {t("agentTabs.wikiKeeper.quiet", { count: p.result.quiet })}
                  </span>
                ) : null}
              </div>
              {p.error ? <p className="text-destructive">{p.error}</p> : null}
              {p.result?.companyNote ? (
                <p className="text-muted-foreground">{p.result.companyNote}</p>
              ) : null}
              {(p.result?.results ?? []).map((r) => (
                <div key={r.project} className="space-y-1 border-t pt-2">
                  <p className="font-medium">{r.project}</p>
                  {r.summary ? <p className="text-muted-foreground">{r.summary}</p> : null}
                  {r.outcomes.length > 0 ? (
                    <ul className="list-disc space-y-0.5 pl-5">
                      {r.outcomes.map((o) => (
                        <OutcomeLine key={`${o.scope}-${o.title}`} o={o} />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground">{t("agentTabs.wikiKeeper.wikiOk")}</p>
                  )}
                  <SeedLine r={r} />
                </div>
              ))}
              {(p.result?.failures ?? []).map((f) => (
                <p key={f.project} className="text-destructive">
                  {f.project}: {f.error}
                </p>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
